// Client-side search index for the Settings screen.
//
// The "Search settings…" box matches against this index to (a) highlight the
// relevant left-nav items and (b) scroll to the matching card. Card anchors are
// derived from the card title via `slugify`, and `SettingsCard` sets the same
// slug as its DOM id — so adding a new card makes it searchable just by listing
// it here; no per-card wiring is needed.

/** Turn a human card title into a stable DOM-id / anchor slug.
 *  "HRF & MPR Settings" → "hrf-mpr-settings". Must match SettingsCard's id. */
export const slugify = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export interface SearchEntry {
  /** Section id — must match a NAV_ITEMS / SECTION_LABELS key. */
  section: string;
  /** DOM anchor of the card (slug of its title). */
  cardId: string;
  /** Card title, shown nowhere but searched. */
  title: string;
  /** Extra synonyms so a search finds the card by intent, not just exact title. */
  keywords: string[];
}

const card = (section: string, title: string, keywords: string[] = []): SearchEntry => ({
  section,
  cardId: slugify(title),
  title,
  keywords,
});

// One entry per card across every section. Keep titles in sync with the cards.
//
// A title here MUST match a rendered <SettingsCard title="…">, because that is
// what produces the DOM id this scrolls to. An entry for a card that does not
// exist looks like a working search result and then silently scrolls nowhere.
// Several such entries had accumulated — "Staff & Roles", the three compliance
// rows, and the Theme & UI / Security / Notifications trio (those three cards
// are hidden for every user, see the NOTE in sections/SystemPreferences.tsx) —
// and are removed here rather than left to rot.
export const SETTINGS_SEARCH_INDEX: SearchEntry[] = [
  card("profile", "My Profile", ["name", "designation", "email", "phone", "avatar"]),
  card("profile", "Profile Photo", ["photo", "avatar", "picture"]),
  card("profile", "Doctor KYC", ["aadhar", "pan", "medical registration", "degree", "verification"]),

  card("devices", "Trusted Devices", ["device", "otp", "trusted", "browser"]),
  card("devices", "Sign Out Everywhere", ["sign out", "logout", "revoke sessions", "lost device"]),

  card("clinic", "Clinic Profile", ["clinic name", "address"]),
  card("clinic", "Clinic Logo", ["logo", "branding"]),
  card("clinic", "Prescription Letterhead", ["letterhead", "header", "prescription pdf"]),
  card("clinic", "Appointment Configuration", ["slot", "booking", "online booking"]),
  card("clinic", "Visit Settings", ["health record", "mandatory vitals", "diagnosis", "hrf", "mpr"]),

  card("access", "Access Control & Permissions", ["staff", "users", "team", "members", "add member"]),
  card("access", "Role Catalog", ["permissions", "roles", "doctor", "receptionist", "admin", "custom role", "clone role", "view only", "edit access"]),

  card("finance", "Subscription Plans", ["plan", "upgrade", "billing plan", "premium"]),
  card("finance", "Payment Settings", ["cash", "upi", "card", "payment methods"]),
  card("finance", "GST Settings", ["gstin", "gst", "tax", "invoice"]),

  card("import", "Import Data", ["import", "upload", "csv", "excel", "practo", "lybrate", "ekacare", "migrate", "data import"]),
  card("import", "Import History", ["past imports", "jobs", "import log"]),

  card("system", "Backup & Restore", ["backup", "restore", "export"]),
];

export interface SearchResult {
  /** Section ids to highlight in the sidebar (label match or has a matching card). */
  sections: Set<string>;
  /** Matching card anchors per section, in index order. */
  cardsBySection: Record<string, string[]>;
  /** Best place to jump to — preferring the section already open. */
  best?: { section: string; cardId: string };
}

// Must cover every nav section, including ones with no card entries above:
// Compliance & Verification is built from plain headings rather than
// SettingsCards, so it has no scroll anchors and a label match is the only way
// search can surface it at all.
const SECTION_LABELS: Record<string, string> = {
  profile:      "Profile & Account",
  devices:      "Trusted Devices",
  clinic:       "Clinic Settings",
  access:       "Access & Personalizations",
  finance:      "Finance & Billing",
  import:       "Data Import",
  compliance:   "Compliance & Verification",
  system:       "System & Preferences",
};

/** Run a query against the index. `activeSection` biases `best` toward the
 *  currently-open section so typing scrolls in place before jumping away. */
export function searchSettings(query: string, activeSection?: string): SearchResult {
  const q = query.trim().toLowerCase();
  const sections = new Set<string>();
  const cardsBySection: Record<string, string[]> = {};

  if (!q) return { sections, cardsBySection };

  // A section is a match if its own label matches the query.
  for (const [id, label] of Object.entries(SECTION_LABELS)) {
    if (label.toLowerCase().includes(q)) sections.add(id);
  }

  // …or if any of its cards match by title or keyword.
  for (const entry of SETTINGS_SEARCH_INDEX) {
    const hit =
      entry.title.toLowerCase().includes(q) ||
      entry.keywords.some(k => k.includes(q));
    if (!hit) continue;
    sections.add(entry.section);
    (cardsBySection[entry.section] ??= []).push(entry.cardId);
  }

  // Prefer a card in the section already open, else the first match overall.
  let best: SearchResult["best"];
  if (activeSection && cardsBySection[activeSection]?.length) {
    best = { section: activeSection, cardId: cardsBySection[activeSection][0] };
  } else {
    const firstSection = SETTINGS_SEARCH_INDEX.find(e => cardsBySection[e.section]?.length);
    if (firstSection) {
      best = { section: firstSection.section, cardId: cardsBySection[firstSection.section][0] };
    }
  }

  return { sections, cardsBySection, best };
}
